import { TrashIcon } from "@heroicons/react/outline";
import { Menu, MenuItem } from "@szhsin/react-menu";
import { compact, omit } from "lodash";
import { useMemo } from "react";
import { FaArrowDown, FaArrowUp } from "react-icons/fa6";
import { MdContentCopy } from "react-icons/md";
import {
  AssessmentQuestionType,
  AssessmentType,
  FullAssessmentGroupSectionInput,
} from "types/graphql-schema";

import Button, { buttonTheme } from "@components/button/button";
import Input from "@components/input/input";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { alphabet } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import AssessmentGroupSidebarQuestion, {
  assessmentQuestionIcon,
} from "./assessment-group-sidebar-question";

const AssessmentGroupSidebarSection = ({
  section,
  sectionIndex,
  canUpdateAssessmentGroup,
  isLastSection,
  onRemoveSection,
  onUpdateSection,
  deleteTooltip,
  onAddQuestion,
  onAddSectionBelow,
  assessmentType,
  onScrollQuestionIntoView,
  onMoveQuestionDown,
  onMoveQuestionUp,
  onDuplicateSection,
  onMoveSectionDown,
  onMoveSectionUp,
}: {
  deleteTooltip?: string;
  section: FullAssessmentGroupSectionInput;
  sectionIndex: number;
  canUpdateAssessmentGroup: boolean;
  assessmentType: AssessmentType;
  isLastSection: boolean;
  onRemoveSection: () => void;
  onUpdateSection: (data: FullAssessmentGroupSectionInput) => void;
  onAddQuestion: (questionType: AssessmentQuestionType) => void;
  onAddSectionBelow: () => void;
  onScrollQuestionIntoView: (
    sectionIndex: number,
    questionIndex: number
  ) => void;
  onMoveQuestionUp: (sectionIndex: number, questionIndex: number) => void;
  onMoveQuestionDown: (sectionIndex: number, questionIndex: number) => void;
  onDuplicateSection: () => void;
  onMoveSectionUp: () => void;
  onMoveSectionDown: () => void;
}) => {
  const addQuestionOptions = useMemo(() => {
    const RangeIcon = assessmentQuestionIcon[AssessmentQuestionType.Range];
    const MultichoiceIcon =
      assessmentQuestionIcon[AssessmentQuestionType.Multichoice];
    const TextIcon = assessmentQuestionIcon[AssessmentQuestionType.Text];
    const NpsIcon = assessmentQuestionIcon[AssessmentQuestionType.Nps];
    return compact([
      {
        value: AssessmentQuestionType.Range,
        label: (
          <div className="flex items-center gap-2">
            <RangeIcon />
            Range
          </div>
        ),
      },
      assessmentType === AssessmentType.EngagementSurvey && {
        value: AssessmentQuestionType.Nps,
        label: (
          <div className="flex items-center gap-2">
            <NpsIcon />
            eNPS
          </div>
        ),
      },
      {
        value: AssessmentQuestionType.Multichoice,
        label: (
          <div className="flex items-center gap-2">
            <MultichoiceIcon />
            Multiple choice
          </div>
        ),
      },
      {
        value: AssessmentQuestionType.Text,
        label: (
          <div className="flex items-center gap-2">
            <TextIcon />
            Short answer
          </div>
        ),
      },
    ]);
  }, [assessmentType]);

  return (
    <div className="border-t p-6" id={`section-${sectionIndex}`}>
      <div className="flex items-center justify-between">
        <div className="text-lg font-semibold">{`Section ${alphabet[sectionIndex]}`}</div>
        <div className="flex items-center gap-2">
          <Button
            disabled={!canUpdateAssessmentGroup || sectionIndex === 0}
            icon={FaArrowUp}
            theme={buttonTheme.iconGray}
            onClick={onMoveSectionUp}
          />
          <Button
            disabled={!canUpdateAssessmentGroup || isLastSection}
            icon={FaArrowDown}
            theme={buttonTheme.iconGray}
            onClick={onMoveSectionDown}
          />
          <Button
            disabled={!canUpdateAssessmentGroup}
            icon={MdContentCopy}
            theme={buttonTheme.iconGray}
            onClick={onDuplicateSection}
          />
          <Button
            disabled={!canUpdateAssessmentGroup || !!deleteTooltip}
            tooltip={deleteTooltip}
            icon={TrashIcon}
            theme={buttonTheme.iconGray}
            onClick={onRemoveSection}
          />
        </div>
      </div>

      <div className="mt-6">
        <Input
          disabled={!canUpdateAssessmentGroup}
          aria-label="Section input"
          className="w-full"
          placeholder="Add header"
          value={section.title ?? ""}
          onChange={(e) => {
            onUpdateSection({
              ...section,
              title: e.target.value,
            });
          }}
        />
        <TextareaWysiwyg
          editable={canUpdateAssessmentGroup}
          className={classNames("mt-4", canUpdateAssessmentGroup && "bg-white")}
          fullWidth={!canUpdateAssessmentGroup}
          placeholder="Add decription (optional)"
          value={section.description}
          deps={[section.title]}
          onChangeValue={(description: string) => {
            onUpdateSection({
              ...section,
              description,
            });
          }}
        />
      </div>

      <div className="mt-6 flex flex-col gap-6">
        {section.questions.map((question, questionIndex) => {
          return (
            <AssessmentGroupSidebarQuestion
              key={questionIndex}
              assessmentType={assessmentType}
              sectionIndex={sectionIndex}
              questionIndex={questionIndex}
              isFirstQuestion={questionIndex === 0 && sectionIndex === 0}
              isLastQuestion={
                section.questions.length - 1 === questionIndex && isLastSection
              }
              question={assertNonNull(question)}
              canUpdateAssessmentGroup={canUpdateAssessmentGroup}
              onUpdateQuestion={(newQuestion) => {
                onUpdateSection({
                  ...section,
                  questions: section.questions.map((q, i) =>
                    i === questionIndex ? newQuestion : q
                  ),
                });
              }}
              onDeleteQuestion={() => {
                onUpdateSection({
                  ...section,
                  questions: section.questions.filter(
                    (_, i) => i !== questionIndex
                  ),
                });
              }}
              onMoveQuestionUp={() => {
                onMoveQuestionUp(sectionIndex, questionIndex);
              }}
              onMoveQuestionDown={() => {
                onMoveQuestionDown(sectionIndex, questionIndex);
              }}
              onDuplicateQuestion={() => {
                onUpdateSection({
                  ...section,
                  questions: [
                    ...section.questions.slice(0, questionIndex),
                    question,
                    {
                      ...omit(question, "id"),
                      title: `Copy of ${question?.title}`,
                    },
                    ...section.questions.slice(questionIndex + 1),
                  ],
                });
                onScrollQuestionIntoView(sectionIndex, questionIndex + 1);
              }}
            />
          );
        })}
      </div>

      <div className="mt-6 flex items-center gap-4">
        <Menu
          className="z-dropdown"
          portal
          transition
          menuButton={
            <Button
              theme={buttonTheme.lightBlue}
              disabled={!canUpdateAssessmentGroup}
            >
              Add Question
              <TopicflowIcon
                type="chevronDown"
                className={classNames(
                  "h-4 w-4 text-gray-400 shrink-0 pointer-events-none"
                )}
              />
            </Button>
          }
        >
          {addQuestionOptions.map((option) => (
            <MenuItem
              key={option.value}
              className="text-sm"
              onClick={() => onAddQuestion(option.value)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>

        <Button
          disabled={!canUpdateAssessmentGroup}
          theme={buttonTheme.lightBlue}
          text="Add section below"
          onClick={onAddSectionBelow}
        />
      </div>
    </div>
  );
};

export default AssessmentGroupSidebarSection;
